import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import UserInitials from "@/components/ui/UserInitials";
import { Check, PauseCircle, PlayCircle, Plus, Trash, UserPlus, Folders, Eye, BellSimpleRinging, LinkSimple, PaperPlaneTilt, ArrowClockwise } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import UserComplianceDocsDialog from "@/components/compliance/UserComplianceDocsDialog";
import InvestorInterestsDialog from "@/components/admin/InvestorInterestsDialog";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "investment_team", label: "Investment Team" },
  { value: "investor", label: "Investor" },
  { value: "client", label: "Client" },
];
const STATUSES = ["active", "pending", "suspended"];
const CLASSIFICATIONS = ["Per Se Professional Investor", "Elective Professional Investor", "Pending Classification", "Rejected"];
const APPROVAL_STATUSES = ["Registration Submitted", "KYC Pending", "Classification Pending", "Compliance Review", "Approved", "Suspended", "Rejected"];

const approvalTone = (s) =>
  s === "Approved" || s === "active" ? "text-emerald-700 dark:text-emerald-400"
  : s === "Suspended" || s === "suspended" || s === "Rejected" ? "text-red-600 dark:text-red-400"
  : "text-amber-700 dark:text-amber-400";

const statusLabel = (s) => {
  if (!s) return "—";
  if (s === "active") return "ACTIVE";
  if (s === "suspended") return "SUSPENDED";
  if (s === "pending") return "PENDING";
  return s.toUpperCase();
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [complianceUser, setComplianceUser] = useState(null);
  const [interestsUser, setInterestsUser] = useState(null);
  const [invitationUser, setInvitationUser] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => {
    try { await api.patch(`/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeUser = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const setClassification = async (id, classification) => {
    try { await api.patch(`/admin/users/${id}/classification?classification=${encodeURIComponent(classification)}`); toast.success("Classification updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setApproval = async (id, status) => {
    try { await api.patch(`/admin/users/${id}/approval?status=${encodeURIComponent(status)}`); toast.success(`Set to ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/users/${id}/status`, { status }); toast.success(`Account ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyWelcomeLink = async (userId, userEmail) => {
    try {
      const r = await api.post(`/admin/users/${userId}/welcome-link`);
      const url = r.data.url;
      // Best-effort clipboard write — fall back to a toast that shows the URL
      // when the browser blocks clipboard access (e.g. insecure context).
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Welcome link copied — valid for ${r.data.expires_in_days} days`);
      } catch {
        toast.info(`Welcome link for ${userEmail}: ${url}`, { duration: 20000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate welcome link");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin · Users</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">User management</h1>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-user-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
          <UserPlus size={16} className="mr-1.5" /> New user
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Classification</TableHead>
              <TableHead>Approval</TableHead>
              <TableHead>Interests</TableHead>
              <TableHead>FCA no.</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              const isInvestor = u.role === "investor";
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.email.split("@")[0]}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserInitials name={u.name} email={u.email} size="h-9 w-9" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm flex items-center gap-2">
                          {u.name}
                          {isSelf && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-bold">You</span>}
                        </div>
                        {u.role === "client" && u.client_company_name && (
                          <Link
                            to={`/companies/${u.client_company_id}`}
                            className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline block truncate"
                            data-testid={`client-company-${u.email.split("@")[0]}`}
                            title={`Client of ${u.client_company_name}`}
                          >
                            Client of {u.client_company_name}
                          </Link>
                        )}
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => setRole(u.id, v)} disabled={isSelf}>
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`role-select-${u.email.split("@")[0]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {isInvestor ? (
                      <Select value={u.classification || "Pending Classification"} onValueChange={(v) => setClassification(u.id, v)}>
                        <SelectTrigger className="h-8 w-[220px]" data-testid={`class-select-${u.email.split("@")[0]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground" aria-label="not applicable">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isInvestor ? (
                      <Select value={u.approval_status || "Registration Submitted"} onValueChange={(v) => setApproval(u.id, v)}>
                        <SelectTrigger className="h-8 w-[210px]" data-testid={`approval-select-${u.email.split("@")[0]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {APPROVAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-xs uppercase tracking-wider font-semibold ${approvalTone(u.status || u.approval_status)}`}>{statusLabel(u.status || u.approval_status)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isInvestor ? (
                      <InterestsCell user={u} onEdit={() => setInterestsUser(u)} />
                    ) : (
                      <span className="text-xs text-muted-foreground" aria-label="not applicable">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isInvestor ? (
                      <FcaNumberCell user={u} onSaved={load} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.last_login ? timeAgo(u.last_login) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      {!isSelf && isInvestor && u.approval_status !== "Approved" && (
                        <Button size="sm" variant="outline" onClick={() => setApproval(u.id, "Approved")} data-testid={`approve-${u.email.split("@")[0]}`}>
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                      )}
                      {!isSelf && isInvestor && u.approval_status === "Approved" && (
                        <Button size="sm" variant="outline" onClick={() => setApproval(u.id, "Suspended")} data-testid={`suspend-${u.email.split("@")[0]}`}>
                          <PauseCircle size={14} className="mr-1" /> Suspend
                        </Button>
                      )}
                      {!isSelf && isInvestor && u.approval_status === "Suspended" && (
                        <Button size="sm" variant="outline" onClick={() => setApproval(u.id, "Approved")} data-testid={`reinstate-${u.email.split("@")[0]}`}>
                          <PlayCircle size={14} className="mr-1" /> Reinstate
                        </Button>
                      )}
                      {/* Non-investor accounts (admins, team, clients): suspend/reinstate via user.status */}
                      {!isSelf && !isInvestor && u.status !== "suspended" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "suspended")} data-testid={`suspend-${u.email.split("@")[0]}`}>
                          <PauseCircle size={14} className="mr-1" /> Suspend
                        </Button>
                      )}
                      {!isSelf && !isInvestor && u.status === "suspended" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "active")} data-testid={`reinstate-${u.email.split("@")[0]}`}>
                          <PlayCircle size={14} className="mr-1" /> Reinstate
                        </Button>
                      )}
                      {!isSelf && isInvestor && (
                        <Link to={`/admin/users/${u.id}/audit`} className="h-8 w-8 rounded grid place-items-center hover:bg-secondary text-muted-foreground hover:text-foreground" data-testid={`audit-${u.email.split("@")[0]}`} title="Audit log">
                          <Eye size={14} />
                        </Link>
                      )}
                      {!isSelf && isInvestor && (
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setComplianceUser(u)} data-testid={`docs-${u.email.split("@")[0]}`} title="Compliance documents">
                          <Folders size={14} />
                        </Button>
                      )}
                      {!isSelf && u.must_change_password && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyWelcomeLink(u.id, u.email)}
                          data-testid={`copy-welcome-${u.email.split("@")[0]}`}
                          title="Copy 7-day password-set link"
                        >
                          <LinkSimple size={14} className="mr-1" /> Copy link
                        </Button>
                      )}
                      {!isSelf && u.must_change_password && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setInvitationUser(u)}
                          data-testid={`send-invite-${u.email.split("@")[0]}`}
                          title="Preview and send an invitation email with the password-set link"
                          className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
                        >
                          <PaperPlaneTilt size={14} className="mr-1" /> Send invitation
                        </Button>
                      )}
                      {!isSelf && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid={`delete-user-${u.email.split("@")[0]}`}>
                              <Trash size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This permanently removes {u.email} and their bookmarks. Meeting requests and enquiries stay for audit purposes.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeUser(u.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <NewUserDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      <UserComplianceDocsDialog open={!!complianceUser} onOpenChange={() => setComplianceUser(null)} user={complianceUser} />
      <InvestorInterestsDialog
        open={!!interestsUser}
        onOpenChange={(o) => !o && setInterestsUser(null)}
        user={interestsUser}
        onSaved={() => load()}
      />
      <InvitationDialog
        user={invitationUser}
        onClose={() => setInvitationUser(null)}
        onSent={() => { setInvitationUser(null); load(); }}
      />
    </div>
  );
}

function InvitationDialog({ user, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [welcomeUrl, setWelcomeUrl] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.get(`/admin/users/${user.id}/welcome-preview`)
      .then((r) => {
        setSubject(r.data.subject);
        // Substitute {{name}} up-front so the admin can see the finished
        // email exactly as the recipient will read it. {{welcome_url}} is
        // preserved as a placeholder so we can regenerate a fresh link if
        // needed without breaking wording.
        setBody((r.data.body || "").replace(/\{\{name\}\}/g, r.data.name || ""));
        setWelcomeUrl(r.data.welcome_url);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load invitation preview");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  if (!user) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/welcome-send`, { subject, body });
      toast.success(`Invitation sent to ${user.email}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setBusy(false);
    }
  };

  const insertLinkPlaceholder = () => setBody((b) => `${b}${b.endsWith("\n") ? "" : "\n\n"}{{welcome_url}}`);
  const regenerate = async () => {
    setRegenBusy(true);
    try {
      const r = await api.get(`/admin/users/${user.id}/welcome-preview`);
      setWelcomeUrl(r.data.welcome_url);
      toast.info("Fresh link generated for this invitation");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate link");
    } finally {
      setRegenBusy(false);
    }
  };

  // Render the merged preview so the admin sees exactly what will be sent.
  const preview = (body || "").replace(/\{\{welcome_url\}\}/g, welcomeUrl || "");

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Send invitation email</DialogTitle>
          <div className="text-sm text-muted-foreground">
            To <span className="font-medium text-foreground">{user.email}</span>
            {user.name ? <> · {user.name}</> : null}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Preparing preview…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="invitation-subject"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Body
                </Label>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button" size="sm" variant="ghost"
                    onClick={insertLinkPlaceholder}
                    className="h-7 text-xs"
                    data-testid="invitation-insert-link"
                  >
                    <LinkSimple size={12} className="mr-1" /> Insert link placeholder
                  </Button>
                  <Button
                    type="button" size="sm" variant="ghost"
                    onClick={regenerate}
                    disabled={regenBusy}
                    className="h-7 text-xs"
                    data-testid="invitation-regen-link"
                  >
                    <ArrowClockwise size={12} className="mr-1" /> {regenBusy ? "Refreshing…" : "New link"}
                  </Button>
                </div>
              </div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                data-testid="invitation-body"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Use <code className="bg-secondary px-1 rounded">{"{{welcome_url}}"}</code> anywhere in the body — it will be replaced with the live password-set link when sent.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Preview (with link)</Label>
              <div
                className="max-h-56 overflow-auto rounded-md border border-border bg-secondary/40 p-3 whitespace-pre-wrap text-sm leading-relaxed"
                data-testid="invitation-preview"
              >
                {preview}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={send}
            disabled={busy || loading || !subject.trim() || !body.trim()}
            data-testid="invitation-send"
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            <PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FcaNumberCell({ user, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(user.fca_number || "");
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/fca-number`, { fca_number: val.trim() });
      toast.success(val.trim() ? "FCA number updated" : "FCA number cleared");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`fca-editing-${user.email.split("@")[0]}`}>
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          autoFocus
          onKeyDown={(e) => { if (e.key === "Enter") save(); else if (e.key === "Escape") { setEditing(false); setVal(user.fca_number || ""); } }}
          className="h-7 text-xs w-[110px]"
          placeholder="123456"
          maxLength={60}
          data-testid={`fca-input-${user.email.split("@")[0]}`}
        />
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={save} disabled={busy} data-testid={`fca-save-${user.email.split("@")[0]}`} title="Save"><Check size={12} /></Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setVal(user.fca_number || ""); setEditing(true); }}
      className="text-xs hover:underline text-left"
      data-testid={`fca-cell-${user.email.split("@")[0]}`}
      title="Click to edit"
    >
      {user.fca_number ? (
        <span className="font-mono">{user.fca_number}</span>
      ) : (
        <span className="text-muted-foreground italic">Add…</span>
      )}
    </button>
  );
}

function InterestsCell({ user, onEdit }) {
  const interests = Array.isArray(user?.interested_industries) ? user.interested_industries : [];
  const shown = interests.slice(0, 2);
  const hidden = interests.length - shown.length;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {interests.length === 0 ? (
        <span className="text-xs text-muted-foreground italic">None set</span>
      ) : (
        <>
          {shown.map((i) => (
            <span key={i} className="inline-flex items-center h-6 px-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[10px] font-semibold tracking-wide">
              {i}
            </span>
          ))}
          {hidden > 0 && (
            <span className="inline-flex items-center h-6 px-2 rounded-full bg-secondary text-muted-foreground text-[10px] font-semibold" title={interests.slice(2).join(", ")}>
              +{hidden}
            </span>
          )}
        </>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={onEdit}
        data-testid={`interests-edit-${user.email.split("@")[0]}`}
        title="Edit industry alerts"
      >
        <BellSimpleRinging size={14} />
      </Button>
    </div>
  );
}

function NewUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "investor", status: "active", company: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Created ${form.name}`);
      setForm({ name: "", email: "", role: "investor", status: "active", company: "", phone: "" });
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Add new user</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Investor" data-testid="new-user-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@fund.com" data-testid="new-user-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="new-user-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Firm / Fund (optional)</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="e.g., Northlake Capital" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block font-semibold">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44…" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="new-user-submit" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            <Plus size={14} className="mr-1" /> {busy ? "Creating…" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
